import { useRoute } from 'vue-router';
import { formatGradeName } from '@/lib/grades';
import { hasMllSupport } from '@/lib/status-utils';
import type {
  NavigationItem,
  UseReportNavigationOptions,
} from '../types/navigation';

export function buildOverviewNavigation(
  reportId: string,
  options: UseReportNavigationOptions,
): NavigationItem[] {
  const route = useRoute();
  const isGradeLevelView =
    route.name === 'report-grade-overview' && route.params.grade;

  const navItems: NavigationItem[] = [];

  // Don't build overview navigation if in grade-level view
  if (isGradeLevelView) {
    return navItems;
  }

  console.log('🏗️ Building overview navigation with:', {
    reportId,
    hasGatewaySnapshots: !!options.report?.gateway_snapshots?.length,
    hasOverviews: !!options.report?.overviews?.length,
    fullReport: options.report,
  });

  // Don't add a separate Gateway Ratings section - gateways are integrated into grade bands

  // Add overview sections based on grade bands (like the old component)
  if (options.report?.overviews?.length) {
    const sortedOverviews = [...options.report.overviews]
      .filter((overview: any) => {
        const hasValidReportCards =
          overview.report_cards && overview.report_cards.length > 0;
        const hasValidTitle = overview.title && overview.title.trim() !== '';
        return hasValidReportCards && hasValidTitle;
      })
      .sort((a: any, b: any) => {
        const aLowestOrder = Math.min(
          ...a.report_cards.map((rc: any) => rc.grade.order),
        );
        const bLowestOrder = Math.min(
          ...b.report_cards.map((rc: any) => rc.grade.order),
        );
        return aLowestOrder - bLowestOrder;
      });

    const hasMultipleOverviews = sortedOverviews.length > 1;

    if (hasMultipleOverviews) {
      // Multiple grade bands - create sections for each (Math K-2, Math 3-5, etc.)
      sortedOverviews.forEach((overview: any, index: number) => {
        const overviewTitle = overview.title || 'Overview';
        const gradeBandName = overviewTitle.replace(' Overview', ''); // e.g., "Math K-2"

        // Get dynamic section titles from report data
        const additionalGroup = overview.criteria_groups?.find(
          (cg: any) =>
            cg.title.includes('Additional Elements') ||
            cg.title.includes('Additional Criteria') ||
            cg.slug === 'additional-criteria' ||
            cg.slug === 'additional-elements',
        );
        const additionalElementsTitle =
          additionalGroup?.title || 'Additional Elements';

        // Extract just the grade part for subsections
        let gradeOnlyName = gradeBandName;
        const subjectPrefixes = ['ELA', 'Math', 'Science'];
        for (const prefix of subjectPrefixes) {
          if (gradeBandName.startsWith(`${prefix} `)) {
            gradeOnlyName = gradeBandName.substring(prefix.length + 1);
            break;
          }
        }

        const overviewChildren: NavigationItem[] = [
          {
            title: `${gradeOnlyName} Gateway Ratings Summary`,
            value: `alignment-usability-${index}`,
            href: `#gateway-ratings-summary-${index}`,
          },
        ];

        // Check if this overview has Science of Reading
        const hasSorData = overview.criteria_groups?.some(
          (cg: any) =>
            cg.title.includes('Science of Reading') ||
            cg.slug === 'science-of-reading',
        );

        if (hasSorData) {
          overviewChildren.push({
            title: `${gradeOnlyName} Science of Reading`,
            value: `science-of-reading-${index}`,
            href: `#science-of-reading-${index}`,
          });
        }

        // Add Additional Elements if they exist
        if (overview.criteria_groups?.length) {
          overviewChildren.push({
            title: `${gradeOnlyName} ${additionalElementsTitle}`,
            value: `additional-data-${index}`,
            href: `#additional-criteria-${index}`,
          });
        }

        navItems.push({
          title: gradeBandName, // Math K-2, Math 3-5, etc.
          value: `overview-${index}`,
          href: `#overview-${index}`,
          indentChildren: true,
          children: overviewChildren,
        });
      });
    } else if (sortedOverviews.length === 1) {
      // Single grade band - create traditional overview section
      const overview = sortedOverviews[0];

      const overviewChildren: NavigationItem[] = [
        {
          title: 'Ratings Snapshot',
          value: 'ratings-snapshot',
          href: '#ratings-snapshot',
        },
        {
          title: 'Gateway Ratings Summary',
          value: 'gateway-ratings-summary',
          href: '#gateway-ratings-summary',
        },
        {
          title: 'Grade Level Overview Summary',
          value: 'grade-level-overview',
          href: '#grade-level-overview',
        },
      ];

      // Add Publication Details if they exist
      if (options.report?.details && options.report.details.length > 0) {
        overviewChildren.push({
          title: 'Publication Details',
          value: 'publication-details',
          href: '#publication-details',
        });
      }

      navItems.push({
        title: 'Overview',
        value: 'overview',
        indentChildren: true,
        children: overviewChildren,
      });
    }
  }

  console.log('🎯 Built overview navigation:', {
    itemCount: navItems.length,
    itemTitles: navItems.map((item) => item.title),
  });

  return navItems;
}
